/* eslint-disable @next/next/no-img-element */
import * as React from 'react'
import { useEffect, useState } from 'react'
import { inject, observer } from 'mobx-react'
import * as eventNames from 'timerevents/eventNames'
import { RootStore } from 'stores'
import { MiniModal } from './MiniModal'
import QuizWithImage from 'emailcaps/QuizWithImage'
import { MiniModalProps } from 'emailcaps/MiniModal'
import { PriceManager } from 'prices'
import { regions } from 'utils/internationalization'
import ReferralModal from './ReferralModal/ReferralModal'
import styles from './EmailCapContainer.module.scss'
import { IconMap } from 'components/_const_assets/icons'
import { trackEmailModalDismissFloatingButtonPress, trackEmailModalFloatingButtonPress } from 'events/index'
import { Button } from 'components/WebEv/Buttons'
import { useRouter } from 'next/router'
import { CaptureLocation } from 'utils/events'
import { EMAIL_SMS_DISCOUNT_AMOUNT, EMAIL_SMS_DISCOUNT_CODE, MEMBERS_DISCOUNT_CODE } from 'stripe_lib/discounts'
import { ExitIntentModal } from './ExitIntentModal/ExitIntentModal'

export enum UTMContent {
	ytshervinshares = 'ytshervinshares',
	ytmeetpod3 = 'ytmeetpod3',
	ytugcsleepfitness = 'ytugcsleepfitness',
	ytwakeupenergized = 'ytwakeupenergized',
}

export interface EmailCapContainerProps {
	rootStore?: RootStore
}

export const isPathInExcludedPages = (path: string) => {
	for (const region of regions) {
		path = path.replace(`/${region}/`, '/')
	}

	if (path.charAt(path.length - 1) !== '/') {
		path = path + '/'
	}

	for (const url of EXCLUDED_PAGES.urls) {
		if (path === url) {
			return true
		}
	}

	return false
}

export const EXCLUDED_PAGES = {
	urls: [
		'/checkout/contact/',
		'/checkout/payment/',
		'/miami/',
		'/referral/',
		'/costco-shows/',
		'/international/',
		'/member-accessories/',
		'/manuals/',
		'/accessibility/',
		'/return-policy/',
		'/warranty/',
		'/press/',
		'/product/premium-mattress/',
		'/vipaccess/',
		'/endel/',
		'/sleep-coaching-onboarding/',
		'/sleep-coaching/',
		'/travel/',
		'/hosts/',
		'/brooke/',
		'/member-referrals/',
		'/hubermanvip/',
		'/referred/',
		'/cart/',
		'/summeronice/',
		'/wall-of-love/',
		'/equinoxspa/',
		'/apl/',
		'/hyperice/',
		'/levels/',
		'/referral-terms-conditions/',
		'/finance/',
		'/future/',
		'/future',
		'/higherdose/',
		'/healf/',
		'/lifetime/',
		'/strava/april/',
		'/login/',
		'/truemed/',
	],
	queryParams: ['utm_source=hospitality_sleep_report', 'member-accessories=1', 'utm_source=klaviyo', 'utm_source=app', 'campaign=attia', 'member=pod1', 'campaign=levels', 'campaign=future'],
}

const renderEmailModal = (modalData: MiniModalProps, isVanity: boolean, isExitIntent: boolean) => {
	if (isExitIntent) {
		return <ExitIntentModal {...modalData} />
	}
	if (isVanity) {
		return <MiniModal {...modalData} />
	}

	return <QuizWithImage {...modalData} />
}

export const EmailCapContainer = inject('rootStore')(
	observer((props: EmailCapContainerProps) => {
		const { modalStore, settingsStore, priceStore } = props.rootStore!
		const key = 'emailCapContainer' + Date.now().toString()
		const [referralModalVisible, setReferralModalVisible] = useState(true)
		const router = useRouter()
		const { utm_medium, utm_source } = router.query
		const utm_content = router.query.utm_content as UTMContent

		useEffect(() => {
			window.addEventListener(eventNames.emailCapModalTrigger, () => modalStore.displayEmailCapModal())
			if (sessionStorage.getItem(eventNames.emailCapModalTriggerDone) && !sessionStorage.getItem(eventNames.emailCapShortcutButtonTriggerDone)) modalStore.forceDisplayEmailCapModalShortcut()
		}, [modalStore])

		const path = window.location.pathname.toLowerCase()
		const queryParams = window.location.search.toLowerCase()
		const redGerard = path === '/red/' || path === '/red'
		const strava = path.includes('/strava')
		const efprocycling = path.includes('/efprocycling')

		const isUSorCA = settingsStore.currentRegion === '' || settingsStore.currentRegion === 'ca'
		const isAU = settingsStore.currentRegion === 'au'
		const { settings } = settingsStore
		const currency = priceStore.currency

		const isVanity = !!(settings && settings.emailCapSettings) || redGerard || strava || efprocycling

		const amount = PriceManager.convertToCurrency(EMAIL_SMS_DISCOUNT_AMOUNT, PriceManager.getRegionCurrency(settingsStore.currentRegion))
		const totalAmount = isVanity ? priceStore.totalProductDiscountNumber : amount + priceStore.totalProductDiscountNumber

		let modalDesktopImage = isAU ? 'https://eightsleep.imgix.net/ecap_au_desktop.jpg?v=1699543504' : 'https://eight-eightsleep-react.s3.us-east-2.amazonaws.com/assets/2024-winter-ecap-desktopv2.jpg'
		let modalMobileImage = isAU ? 'https://eightsleep.imgix.net/ecap_au_mobile.jpg?v=1699543504' : 'https://eight-eightsleep-react.s3.us-east-2.amazonaws.com/assets/2024-winter-ecap-mobilev2.jpg'

		const isytshervinshares = utm_content === UTMContent.ytshervinshares || (utm_medium === 'cpm' && utm_source === 'facebook' && !![657510927517].find((it) => router.asPath.includes(it.toString())))
		const isytmeetpod3 =
			utm_content === UTMContent.ytmeetpod3 ||
			utm_content === UTMContent.ytugcsleepfitness ||
			utm_content === UTMContent.ytwakeupenergized ||
			(utm_medium === 'cpm' && utm_source === 'facebook' && !![637009028806, 658234053319, 641722010309].find((it) => router.asPath.includes(it.toString())))
		if (isytshervinshares) {
			modalDesktopImage = 'https://eightsleep.imgix.net/shervin_ecap_desktop.jpg?v=1687441520'
			modalMobileImage = 'https://eightsleep.imgix.net/shervin_ecap_mobile.jpg?v=1687441520'
		} else if (isytmeetpod3) {
			modalDesktopImage = 'https://eightsleep.imgix.net/ytmeetpod3_ecap_desktop.jpg?v=1687441520'
			modalMobileImage = 'https://eightsleep.imgix.net/ytmeetpod3_ecap_mobile.jpg?v=1687441520'
		}

		const defaultConfig = {
			subtitle: settings.saleName,
			description: 'Sign up to kick off your sleep fitness journey with an exclusive offer.',
			imgUrl: modalDesktopImage,
			imgAlt: '',
			buttonText: 'Join the Club',
			dismissText: "I don't like good sleep",
		}

		let modalData: MiniModalProps = {
			...defaultConfig,
			title: '',
			captureLocation: CaptureLocation.MODAL,
			emailCapModalVisible: modalStore.emailCapModalVisible,
			hideEmailCapModal: modalStore.hideEmailCapModal,
			settings: settings,
			preventMouse: !isUSorCA,
			currency: currency,
			amount: PriceManager.formatPriceToCurrencyNoDecimal(amount, currency),
			desktopImg: modalDesktopImage,
			mobileImg: modalMobileImage,
			discountCode: EMAIL_SMS_DISCOUNT_CODE.toLowerCase(),
			totalDiscount: PriceManager.formatPriceToCurrencyNoDecimal(totalAmount, currency),
			region: settingsStore.currentRegion,
		}

		if (
			path === '/referral/' ||
			path.includes('/thank_you/') ||
			path.includes('/checkout/') ||
			settings.promoCode.endsWith('50') ||
			settings.promoCode === EMAIL_SMS_DISCOUNT_CODE.toLowerCase() ||
			settings.promoCode.toUpperCase() === MEMBERS_DISCOUNT_CODE ||
			(settings.promoCode !== 'REFFRB' && (isPathInExcludedPages(path) || EXCLUDED_PAGES.queryParams.find((it) => queryParams.includes(it))))
		) {
			return null
		}

		if (isVanity) {
			// const additionalVanityDiscount = PriceManager.convertToCurrency(EMAIL_SMS_DISCOUNT_AMOUNT, PriceManager.getRegionCurrency(settingsStore.currentRegion))
			modalData = {
				...modalData,
				captureLocation: CaptureLocation.MODAL,
				key: 'Vanity Modal ' + key,
				...{
					title: null,
					amount: null,
					subtitle: '',
					description: null,
					imgUrl: settings.emailCapSettings?.imageSrc || defaultConfig.imgUrl,
					imgAlt: settings.emailCapSettings?.imageAlt || defaultConfig.imgAlt,
				},
			}
		}

		if (modalStore.showExitIntent) {
			modalData = {
				...modalData,
				key: 'Exit Modal ' + key,
				captureLocation: CaptureLocation.MODAL,
				title: `Want to unlock ${modalData.totalDiscount} off Pod 4 Ultra?`,
				dismissText: 'I’ll pay full price',
				...{
					imgUrl: 'https://eight-eightsleep-react.s3.us-east-2.amazonaws.com/assets/2024-phantom-launch/pod4-hub-front.png',
					imgAlt: 'Exit Intent caption',
					settings: {
						...modalData.settings,
					},
				},
			}
		}

		if (redGerard) {
			modalData = {
				...modalData,
				key: 'Vanity Modal ' + key,
				captureLocation: CaptureLocation.MODAL,
				...{
					title: `Unlock up to`,
					subtitle: '',
					description: "Red Gerard's secret to make everyday feel like powder day: the Pod. Enter your email to start your sleep fitness journey.",
					imgUrl: 'https://eightsleep.imgix.net/BLATT--Red-453A6829.jpg?v=1670879192',
					imgAlt: 'Red Gerard',
				},
			}
		}

		if (strava) {
			modalData = {
				...modalData,
				key: 'Vanity Modal ' + key,
				captureLocation: CaptureLocation.MODAL,
				title: `Get an additional ${PriceManager.formatPriceToCurrencyNoDecimal(EMAIL_SMS_DISCOUNT_AMOUNT, currency)} off`,
				...{
					imgUrl: 'https://eight-eightsleep-react.s3.us-east-2.amazonaws.com/assets/strava/strava-ecap.jpg',
					imgAlt: 'Strava caption',
					settings: {
						...modalData.settings,
						partnerName: 'Strava',
						saleName: 'Strava exclusive',
					},
				},
			}
		}

		if (efprocycling) {
			modalData = {
				...modalData,
				key: 'Vanity Modal ' + key,
				captureLocation: CaptureLocation.MODAL,
				title: `Get an additional ${PriceManager.formatPriceToCurrencyNoDecimal(EMAIL_SMS_DISCOUNT_AMOUNT, currency)} off`,
				...{
					imgUrl: 'https://eight-eightsleep-react.s3.us-east-2.amazonaws.com/assets/strava/efprocycling-ecap.jpg',
					imgAlt: 'EF Pro Cycling caption',
					settings: {
						...modalData.settings,
						partnerName: 'EF Pro Cycling',
						saleName: 'EF Pro Cycling exclusive',
					},
				},
			}
		}

		const showReferralEcap =
			path.includes('referral') ||
			(path.includes('/pod-cover') &&
				typeof window !== 'undefined' &&
				(window.location.search.includes('utm_source=friendbuy') || window.location.search.includes('utm_campaign=friendbuy') || settings.promoCode === 'REFFRB'))

		const signedUpReferral = typeof window !== 'undefined' ? settings.promoCode === 'REFFRB' && sessionStorage.getItem('signedUpReferral') : false
		const displayShortcut =
			typeof window !== 'undefined' &&
			!modalStore.emailCapModalVisible &&
			modalStore.hideEmailCapModal &&
			modalStore.showEmailCapModalShortcut &&
			sessionStorage.getItem(eventNames.emailCapModalTriggerDone) &&
			!sessionStorage.getItem(eventNames.emailCapShortcutButtonTriggerDone) &&
			localStorage.getItem('email') == null

		if (path.includes('/product')) {
			document.documentElement.style.setProperty('--promo-bar-height', (document.getElementById('sticky-atc')?.clientHeight || 100) + 'px')
		} else if (path.includes('/pod-cover') || path.includes('/pod-membership')) {
			document.documentElement.style.setProperty('--promo-bar-height', settingsStore.coverStickyHeight + 'px')
		} else {
			document.documentElement.style.setProperty('--promo-bar-height', (document.getElementById('sticky-atc')?.clientHeight || 0) + 'px')
		}

		return !showReferralEcap ? (
			<div
				id="email_cap_root"
				className={modalStore.emailCapModalVisible ? 'ecap_modal_visible' : ''}
			>
				{renderEmailModal(modalData, isVanity, modalStore.showExitIntent)}
				{displayShortcut ? (
					<Button.Primary
						id={'floating-modal'}
						size={'compact'}
						className={styles.shortcut}
						onClick={() => {
							trackEmailModalFloatingButtonPress()
							modalStore.forcedisplayEmailCapModal()
						}}
					>
						Get up to {modalData.totalDiscount} off
						<img
							src={IconMap.x}
							role="button"
							alt="close"
							onClick={(e: React.MouseEvent) => {
								e.stopPropagation()
								trackEmailModalDismissFloatingButtonPress()
								modalStore.preventDisplayEmailCapModalShortcut()
							}}
						/>
					</Button.Primary>
				) : null}
			</div>
		) : (
			<div
				id="email_cap_root"
				className={modalStore.emailCapModalVisible ? 'ecap_modal_visible' : ''}
			>
				<ReferralModal
					modalVisible={!signedUpReferral && referralModalVisible}
					currency={currency}
					saleName={settings.saleName}
					handleClose={() => {
						setReferralModalVisible(false)
						sessionStorage.setItem('signedUpReferral', 'true')
					}}
				/>
			</div>
		)
	})
)

export default EmailCapContainer
